import { useEffect, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { CollapseWithIcon } from 'shared/ui'
import { type Integration } from 'entities/Integrations'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useIntegrationStoreMemo } from 'pages/settings/pages/integrations/pages/integrationSettings/hooks/useIntegrationStoreMemo'
import { IntegrationSalesforceSettingsProperty } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSalesforceSettings/ui/IntegrationSalesforceSettingsProperty/IntegrationSalesforceSettingsProperty'
import { IntegrationSalesforceSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSalesforceSettings/store/IntegrationSalesforceSettingsStore'
import { IntegrationSalesforceSettingsSync } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSalesforceSettings/ui/IntegrationSalesforceSettingsSync/IntegrationSalesforceSettingsSync'
import { IntegrationSalesforceSettingsInstallation } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSalesforceSettings/ui/IntegrationSalesforceSettingsInstallation/IntegrationSalesforceSettingsInstallation'
import { CollapseIntegrationSettingsUsers } from 'pages/settings/pages/integrations/pages/integrationSettings/ui/CollapseIntegrationSettingsUsers/CollapseIntegrationSettingsUsers'
import { CollapseIntegrationSettingsTimeline } from 'pages/settings/pages/integrations/pages/integrationSettings/ui/CollapseIntegrationSettingsTimeline/CollapseIntegrationSettingsTimeline'
import { CollapseIntegrationSettingsWebhook } from 'pages/settings/pages/integrations/pages/integrationSettings/ui/CollapseIntegrationSettingsWebhook/CollapseIntegrationSettingsWebhook'
import sharedStyles from 'pages/settings/pages/integrations/pages/integrationSettings/ui/styles.module.scss'

type IIntegrationHubspotSettingsProps = {
  integration: Integration
}

export const IntegrationSalesforceSettings: FC<IIntegrationHubspotSettingsProps> = observer(
  ({ integration }) => {
    const entities = useEntitiesContext()
    const {
      collapseStore,
      installationStore,
      webhookStore,
      syncStore,
      timelineStore,
      userStore,
      propertyStore,
    } = useIntegrationStoreMemo(IntegrationSalesforceSettingsStore, integration, entities)
    const isConnected = integration.isConnected

    useEffect(() => {
      if (isConnected) {
        collapseStore.onToggle(null)()
      }
      if (!isConnected && !collapseStore.opened('installation')) {
        collapseStore.onToggle('installation')()
      }
    }, [isConnected, collapseStore])

    return (
      <div>
        <CollapseWithIcon
          opened={collapseStore.opened('installation')}
          onClick={collapseStore.onToggle('installation')}
          icon={'settingsOutline'}
          title={'Installation'}
          label={`Send ${integration.name} contacts into your Salesmsg account`}
          headerClassName={sharedStyles.collapseWithIcon}
        >
          <IntegrationSalesforceSettingsInstallation store={installationStore} />
        </CollapseWithIcon>
        {isConnected && (
          <>
            <CollapseIntegrationSettingsUsers collapseStore={collapseStore} userStore={userStore} />
            <CollapseWithIcon
              opened={collapseStore.opened('sync')}
              onClick={collapseStore.onToggle('sync')}
              icon={'arrowHorizontalOutline'}
              title={'Contact syncing'}
              label={syncStore.getLabel}
              headerClassName={sharedStyles.collapseWithIcon}
            >
              <IntegrationSalesforceSettingsSync store={syncStore} />
            </CollapseWithIcon>
            <CollapseWithIcon
              opened={collapseStore.opened('property')}
              onClick={collapseStore.onToggle('property')}
              icon={'workflowOutline'}
              title={'Field mapping'}
              label={`${propertyStore.mappedLength} fields mapped`}
              headerClassName={sharedStyles.collapseWithIcon}
            >
              <IntegrationSalesforceSettingsProperty store={propertyStore} />
            </CollapseWithIcon>
            <CollapseIntegrationSettingsTimeline
              collapseStore={collapseStore}
              name={integration.name}
              dailyBundles={timelineStore.logActivity}
              onDailyBundlesToggle={timelineStore.onLogActivityToggle}
            />
            <CollapseIntegrationSettingsWebhook
              collapseStore={collapseStore}
              webhookStore={webhookStore}
            />
          </>
        )}
      </div>
    )
  }
)
